"use client";

import type { ReactNode } from "react";
import { usePathname } from "next/navigation";

import MonarchFallback from "@/components/MonarchFallback";
import BaseNavBar from "@/components/NavBar/BaseNavBar";
import { OFFERS_NAVIGATION } from "@/components/NavBar/offers";
import { PolarisFooter, PolarisHeader } from "@/components/Polaris";
import { normalizedBasePath } from "@/helpers/basePath";
import DynamicMonarchSlot from "../DynamicMonarchSlot";

interface CohesionAppProps {
  children: ReactNode;
}

const normalizePathname = (pathname: string | null): string => {
  const normalizedPathname = (pathname ?? "/").replace(/\/$/, "");

  return normalizedPathname || "/";
};

const isBaseSitePathname = (pathname: string | null): boolean => {
  const normalizedPathname = normalizePathname(pathname);

  // Support both local Next.js routing (`/`) and deployed base-path routing.
  return (
    normalizedPathname === "/" ||
    (normalizedBasePath !== "" && normalizedPathname === normalizedBasePath)
  );
};

export default function App({ children }: CohesionAppProps) {
  const pathname = usePathname();
  const normalizedPathname = normalizePathname(pathname);
  const isOffersPage =
    normalizedPathname === "/deals/offers" ||
    normalizedPathname === `${normalizedBasePath}/deals/offers`;
  const shouldUseBaseNavBar = isBaseSitePathname(pathname);
  const defaultNavigation = isOffersPage ? (
    <BaseNavBar data={OFFERS_NAVIGATION} />
  ) : shouldUseBaseNavBar ? (
    <BaseNavBar />
  ) : (
    <PolarisHeader className="block min-h-16 lg:min-h-20" />
  );

  return (
    <>
      <DynamicMonarchSlot
        slotId="comcastWestRvAlphaNavigation"
        fallback={<MonarchFallback height={["80px", "80px", "64px"]} />}
      >
        {defaultNavigation}
      </DynamicMonarchSlot>
      <main className="overflow-x-clip">{children}</main>
      <DynamicMonarchSlot
        slotId="comcastWestRvAlphaReturnVisitorModal"
        deferUntilIdle
      />
      <DynamicMonarchSlot
        slotId="comcastWestRvAlphaInactivityModal"
        deferUntilIdle
      />
      <DynamicMonarchSlot slotId="comcastWestRvAlphaFooter" deferUntilIdle>
        <PolarisFooter />
      </DynamicMonarchSlot>
      <div id="comcast-rv-cookie-preferences" />
    </>
  );
}
